import React, { useEffect, useRef, useState, useCallback } from 'react'
import { motion, useAnimationFrame, useReducedMotion } from 'framer-motion'

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  color: string
}

interface Connection {
  from: Particle
  to: Particle
  opacity: number
  flow: number
}


export default function NetworkParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [particles, setParticles] = useState<Particle[]>([])
  const [, setConnections] = useState<Connection[]>([])
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const frameRef = useRef(0)
  
  // Detect if we're in dark mode
  const isDarkMode = document.documentElement.classList.contains('dark')


  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDimensions({ width: rect.width, height: rect.height })
    }
  }, [])

  useEffect(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [updateDimensions])

  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return

    const newParticles: Particle[] = []
    
    // Create background particles with green accent
    for (let i = 0; i < 30; i++) {
      // Mix of blue and green particles
      const useGreen = Math.random() > 0.6
      newParticles.push({
        id: i,
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        color: isDarkMode
          ? (useGreen
              ? `rgba(187, 209, 52, ${Math.random() * 0.5 + 0.2})`  // Green #BBD134
              : `rgba(59, 130, 246, ${Math.random() * 0.5 + 0.2})`) // Blue
          : (useGreen
              ? `rgba(155, 173, 43, ${Math.random() * 0.6 + 0.4})`  // Darker green for light mode
              : `rgba(30, 64, 175, ${Math.random() * 0.6 + 0.4})`)  // Blue
      })
    }


    setParticles(newParticles)
  }, [dimensions])

  useAnimationFrame(() => {
    if (!canvasRef.current || particles.length === 0) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Update particles
    const updatedParticles = particles.map(particle => {
      const newX = particle.x + particle.vx
      const newY = particle.y + particle.vy

      // Bounce off walls
      if (newX <= 0 || newX >= dimensions.width) particle.vx *= -1
      if (newY <= 0 || newY >= dimensions.height) particle.vy *= -1


      // Apply velocity dampening
      particle.vx *= 0.999
      particle.vy *= 0.999

      return {
        ...particle,
        x: Math.max(0, Math.min(dimensions.width, newX)),
        y: Math.max(0, Math.min(dimensions.height, newY))
      }
    })

    // Find connections
    const newConnections: Connection[] = []
    for (let i = 0; i < updatedParticles.length; i++) {
      for (let j = i + 1; j < updatedParticles.length; j++) {
        const p1 = updatedParticles[i]
        const p2 = updatedParticles[j]
        const distance = Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2)
        
        if (distance < 100) {
          const opacity = (100 - distance) / 100 * 0.3
          newConnections.push({
            from: p1,
            to: p2,
            opacity,
            flow: frameRef.current * 0.01
          })
        }
      }
    }

    // Draw connections
    newConnections.forEach(connection => {
      ctx.beginPath()
      ctx.moveTo(connection.from.x, connection.from.y)
      ctx.lineTo(connection.to.x, connection.to.y)
      
      const gradient = ctx.createLinearGradient(
        connection.from.x, connection.from.y,
        connection.to.x, connection.to.y
      )
      if (isDarkMode) {
        gradient.addColorStop(0, `rgba(59, 130, 246, ${connection.opacity})`)
        gradient.addColorStop(0.5, `rgba(187, 209, 52, ${connection.opacity * 1.5})`)  // Green accent
        gradient.addColorStop(1, `rgba(59, 130, 246, ${connection.opacity})`)
      } else {
        gradient.addColorStop(0, `rgba(30, 64, 175, ${connection.opacity * 0.8})`)
        gradient.addColorStop(0.5, `rgba(155, 173, 43, ${connection.opacity * 1.2})`)  // Green accent
        gradient.addColorStop(1, `rgba(30, 64, 175, ${connection.opacity * 0.8})`)
      }
      
      ctx.strokeStyle = gradient
      ctx.lineWidth = 1
      ctx.stroke()

      // Draw animated flow dots
      const flowPos = (Math.sin(connection.flow) + 1) / 2
      const flowX = connection.from.x + (connection.to.x - connection.from.x) * flowPos
      const flowY = connection.from.y + (connection.to.y - connection.from.y) * flowPos
      
      ctx.beginPath()
      ctx.arc(flowX, flowY, 2, 0, 2 * Math.PI)
      ctx.fillStyle = isDarkMode 
        ? `rgba(255, 255, 255, ${connection.opacity * 2})`
        : `rgba(30, 64, 175, ${connection.opacity * 3})`
      ctx.fill()
    })

    // Draw particles
    updatedParticles.forEach(particle => {
      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.size, 0, 2 * Math.PI)
      ctx.fillStyle = particle.color
      ctx.fill()
      
      // Add glow effect
      ctx.shadowBlur = 10
      ctx.shadowColor = particle.color
      ctx.fill()
      ctx.shadowBlur = 0
    })

    setParticles(updatedParticles)
    setConnections(newConnections)
    frameRef.current += 1
  })


  return (
    <div
      ref={containerRef}
      className="relative w-full h-48 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-100 via-blue-100 to-indigo-200 dark:from-gray-900 dark:via-slate-900 dark:to-black"
      style={{ minHeight: '12rem' }}
    >
      {/* Canvas for particle system */}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute inset-0 z-10"
        style={{ width: dimensions.width, height: dimensions.height }}
      />


      {/* Overlay Content */}
      <div className="absolute inset-0 z-30 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-gray-800/90 dark:text-white/90"
          >
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-cyan-400 via-[#BBD134] to-blue-400 bg-clip-text text-transparent">
              Connected Infrastructure
            </h3>
            <p className="text-lg text-gray-700/80 dark:text-white/70 max-w-2xl mx-auto">
              Experience the future of networking with our interactive demo environment
            </p>
          </motion.div>
        </div>
      </div>


      {/* Corner decorations with green accent */}
      <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#BBD134] dark:border-[#BBD134] z-40" />
      <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#BBD134] dark:border-[#BBD134] z-40" />
      <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#BBD134] dark:border-[#BBD134] z-40" />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#BBD134] dark:border-[#BBD134] z-40" />
    </div>
  )
}